// Cache utility - uses IndexedDB for production (file-like), localStorage for development
const isDevelopment = process.env.NODE_ENV === 'development';

// IndexedDB setup for production (file-like storage)
let db = null;
const DB_NAME = 'redhand_cache';
const DB_VERSION = 1;
const STORE_NAME = 'cache';

const initIndexedDB = () => {
  return new Promise((resolve, reject) => {
    if (db) {
      resolve(db);
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => {
      db = request.result;
      resolve(db);
    };

    request.onupgradeneeded = (event) => {
      const database = event.target.result;
      if (!database.objectStoreNames.contains(STORE_NAME)) {
        database.createObjectStore(STORE_NAME);
      }
    };
  });
};

// Get from cache
export const getCache = async (key) => {
  try {
    if (isDevelopment) {
      // Use localStorage in development
      const item = localStorage.getItem(`cache_${key}`);
      if (!item) return null;
      const parsed = JSON.parse(item);
      // Check expiry
      if (parsed.expiry && Date.now() > parsed.expiry) {
        localStorage.removeItem(`cache_${key}`);
        return null;
      }
      return parsed.data;
    } else {
      // Use IndexedDB in production (file-like)
      const database = await initIndexedDB();
      return new Promise((resolve, reject) => {
        const transaction = database.transaction([STORE_NAME], 'readonly');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.get(key);
        
        request.onerror = () => reject(request.error);
        request.onsuccess = () => {
          const result = request.result;
          if (!result) {
            resolve(null);
            return;
          }
          // Check expiry
          if (result.expiry && Date.now() > result.expiry) {
            // Remove expired entry
            const deleteTransaction = database.transaction([STORE_NAME], 'readwrite');
            deleteTransaction.objectStore(STORE_NAME).delete(key);
            resolve(null);
            return;
          }
          resolve(result.data);
        };
      });
    }
  } catch (error) {
    console.warn('Cache get error:', error);
    return null;
  }
};

// Set cache
export const setCache = async (key, data, ttl = 5 * 60 * 1000) => {
  try {
    const expiry = Date.now() + ttl;
    
    if (isDevelopment) {
      // Use localStorage in development
      localStorage.setItem(`cache_${key}`, JSON.stringify({ data, expiry }));
    } else {
      // Use IndexedDB in production (file-like)
      const database = await initIndexedDB();
      return new Promise((resolve, reject) => {
        const transaction = database.transaction([STORE_NAME], 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.put({ data, expiry }, key);
        
        request.onerror = () => reject(request.error);
        request.onsuccess = () => resolve();
      });
    }
  } catch (error) {
    console.warn('Cache set error:', error);
  }
};

// Clear cache
export const clearCache = async (key) => {
  try {
    if (isDevelopment) {
      localStorage.removeItem(`cache_${key}`);
    } else {
      const database = await initIndexedDB();
      return new Promise((resolve, reject) => {
        const transaction = database.transaction([STORE_NAME], 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.delete(key);
        
        request.onerror = () => reject(request.error);
        request.onsuccess = () => resolve();
      });
    }
  } catch (error) {
    console.warn('Cache clear error:', error);
  }
};

// Clear all cache
export const clearAllCache = async () => {
  try {
    if (isDevelopment) {
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('cache_')) {
          localStorage.removeItem(key);
        }
      });
    } else {
      const database = await initIndexedDB();
      return new Promise((resolve, reject) => {
        const transaction = database.transaction([STORE_NAME], 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.clear();
        
        request.onerror = () => reject(request.error);
        request.onsuccess = () => resolve();
      });
    }
  } catch (error) {
    console.warn('Cache clear all error:', error);
  }
};
